<template>
  <div class="print-generator-wrapper" @mouseup="stopDrag" @mouseleave="stopDrag" @mousemove="onDrag">
    <iframe ref="printFrame" style="position: absolute; width: 0; height: 0; border: none; visibility: hidden"></iframe>

    <v-container fluid class="pa-0 full-height-container">
      <v-row class="fill-height ma-0">
        <v-col cols="12" md="4" lg="3" class="editor-sidebar pa-0">
          <v-card tile flat class="d-flex flex-column fill-height grey darken-4">
            <div class="pa-4 overflow-y-auto flex-grow-1 custom-scrollbar" style="height: 90vh">
              <div class="section-label mb-2 mt-2">PAPIERFORMAT</div>
              <v-btn-toggle v-model="config.format" mandatory class="mb-2 d-flex w-100" dark dense color="primary" background-color="grey darken-3">
                <v-btn value="a5" class="flex-grow-1">A5</v-btn>
                <v-btn value="a4" class="flex-grow-1">A4</v-btn>
                <v-btn value="a3" class="flex-grow-1">A3</v-btn>
              </v-btn-toggle>
              <v-btn-toggle
                v-model="config.orientation"
                mandatory
                class="mb-6 d-flex w-100"
                dark
                dense
                color="primary"
                background-color="grey darken-3">
                <v-btn value="portrait" class="flex-grow-1"><v-icon small left>mdi-crop-portrait</v-icon> Hoch</v-btn>
                <v-btn value="landscape" class="flex-grow-1"><v-icon small left>mdi-crop-landscape</v-icon> Quer</v-btn>
              </v-btn-toggle>

              <v-divider class="grey darken-3 mb-6"></v-divider>

              <div class="element-box mb-3" :class="{ active: selectedElement === 'text1' }" @click="selectedElement = 'text1'">
                <div class="d-flex justify-space-between align-center mb-1">
                  <div class="caption font-weight-bold primary--text">1. TITEL (GROSS)</div>
                  <div class="caption grey--text">{{ style.text1.size }}pt</div>
                </div>
                <v-textarea
                  v-model="content.text1"
                  rows="2"
                  auto-grow
                  dense
                  outlined
                  dark
                  hide-details
                  placeholder="Titel"
                  class="mb-2 input-dense"></v-textarea>
                <div v-if="selectedElement === 'text1'">
                  <div class="d-flex align-center mt-2">
                    <v-icon small class="mr-2 grey--text">mdi-format-size</v-icon>
                    <v-slider v-model="style.text1.size" :min="style.text2.size + 5" max="150" dense hide-details class="align-center"></v-slider>
                  </div>
                  <div class="color-dots mt-2">
                    <div v-for="c in colors" :key="c" class="dot" :style="{ background: c }" @click.stop="style.text1.color = c"></div>
                  </div>
                </div>
              </div>

              <div class="element-box mb-3" :class="{ active: selectedElement === 'text2' }" @click="selectedElement = 'text2'">
                <div class="d-flex justify-space-between align-center mb-1">
                  <div class="caption font-weight-bold white--text">2. SUBTITEL (MITTEL)</div>
                  <div class="caption grey--text">{{ style.text2.size }}pt</div>
                </div>
                <v-textarea
                  v-model="content.text2"
                  rows="3"
                  auto-grow
                  dense
                  outlined
                  dark
                  hide-details
                  placeholder="Haupttext"
                  class="mb-2 input-dense"></v-textarea>
                <div v-if="selectedElement === 'text2'">
                  <div class="d-flex align-center mt-2">
                    <v-icon small class="mr-2 grey--text">mdi-format-size</v-icon>
                    <v-slider
                      v-model="style.text2.size"
                      :min="style.text3.size + 5"
                      :max="style.text1.size - 5"
                      dense
                      hide-details
                      class="align-center"></v-slider>
                  </div>
                  <div class="color-dots mt-2">
                    <div v-for="c in colors" :key="c" class="dot" :style="{ background: c }" @click.stop="style.text2.color = c"></div>
                  </div>
                </div>
              </div>

              <div class="element-box mb-6" :class="{ active: selectedElement === 'text3' }" @click="selectedElement = 'text3'">
                <div class="d-flex justify-space-between align-center mb-1">
                  <div class="caption font-weight-bold grey--text">3. INFO (KLEIN)</div>
                  <div class="caption grey--text">{{ style.text3.size }}pt</div>
                </div>
                <v-textarea
                  v-model="content.text3"
                  rows="2"
                  auto-grow
                  dense
                  outlined
                  dark
                  hide-details
                  placeholder="Zusatzinfo"
                  class="mb-2 input-dense"></v-textarea>
                <div v-if="selectedElement === 'text3'">
                  <div class="d-flex align-center mt-2">
                    <v-icon small class="mr-2 grey--text">mdi-format-size</v-icon>
                    <v-slider v-model="style.text3.size" min="10" :max="style.text2.size - 5" dense hide-details class="align-center"></v-slider>
                  </div>
                  <div class="color-dots mt-2">
                    <div v-for="c in colors" :key="c" class="dot" :style="{ background: c }" @click.stop="style.text3.color = c"></div>
                  </div>
                </div>
              </div>

              <v-divider class="grey darken-3 mb-6"></v-divider>

              <div class="section-label mb-2">GRAFIKEN</div>
              <div class="d-flex mb-3">
                <v-btn color="grey darken-3" class="flex-grow-1 mr-1" dark depressed small @click="$refs.fileInput.click()"
                  ><v-icon left small>mdi-upload</v-icon> Upload</v-btn
                >
                <v-btn color="grey darken-3" class="flex-grow-1 ml-1" dark depressed small @click="templateDialog = true"
                  ><v-icon left small>mdi-view-grid</v-icon> Vorlage</v-btn
                >
                <input type="file" ref="fileInput" style="display: none" accept="image/*" @change="onFileChange" />
              </div>
              <v-btn v-if="content.image" block color="red darken-3" dark depressed small @click="removeImage" class="mb-3">Bild entfernen</v-btn>
              <v-btn block color="grey darken-3" dark depressed small @click="logoDialog = true">Logo wählen</v-btn>
              <div class="pa-4 mt-2 grey darken-3">
                <v-btn block color="#00943c" large dark depressed @click="print" :loading="isPrinting">
                  <v-icon left>mdi-printer</v-icon> Drucken
                </v-btn>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="8" lg="9" class="preview-area d-flex align-center justify-center">
          <div class="paper-scaler" :style="{ transform: `scale(${previewScale})` }">
            <div class="paper" :class="[config.format, config.orientation]" ref="paper">
              <div class="image-stripe-bg"></div>
              <div class="image-shape-container">
                <div
                  class="image-fill"
                  :style="
                    content.image
                      ? {
                          backgroundImage: 'url(' + content.image + ')',
                          backgroundPosition: imgPos.x + '% ' + imgPos.y + '%',
                          backgroundSize: 'cover',
                        }
                      : {}
                  "
                  @mousedown.stop="startDrag($event, 'image')"
                  :class="{ 'cursor-move': content.image }">
                  <div v-if="!content.image" class="placeholder-text">BILD</div>
                  <div class="image-drag-hint" v-if="content.image"><v-icon color="white">mdi-cursor-move</v-icon></div>
                </div>
              </div>

              <img :src="content.logo" class="header-logo" alt="Logo" />

              <div
                class="draggable-text text-level-1"
                :class="{ 'is-active': selectedElement === 'text1' }"
                :style="getTextStyle('text1')"
                @mousedown.stop="startDrag($event, 'text1')">
                <span>{{ content.text1 || "TITEL" }}</span>
                <div class="drag-guide"><v-icon x-small>mdi-arrow-up-down</v-icon></div>
              </div>

              <div
                class="draggable-text text-level-2"
                :class="{ 'is-active': selectedElement === 'text2' }"
                :style="getTextStyle('text2')"
                @mousedown.stop="startDrag($event, 'text2')">
                <span>{{ content.text2 || "Hier steht dein Haupttext." }}</span>
                <div class="drag-guide"><v-icon x-small>mdi-arrow-up-down</v-icon></div>
              </div>

              <div
                v-if="content.text3"
                class="draggable-text text-level-3"
                :class="{ 'is-active': selectedElement === 'text3' }"
                :style="getTextStyle('text3')"
                @mousedown.stop="startDrag($event, 'text3')">
                <span>{{ content.text3 }}</span>
                <div class="drag-guide"><v-icon x-small>mdi-arrow-up-down</v-icon></div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="logoDialog" max-width="600"
      ><v-card dark
        ><v-card-title>Logo wählen</v-card-title
        ><v-card-text
          ><v-container fluid
            ><v-row
              ><v-col cols="6" sm="4" v-for="(l, i) in availableLogos" :key="i"
                ><v-card
                  hover
                  @click="setLogo(l)"
                  :class="{ 'active-logo': content.logo === l.path }"
                  class="pa-2 white d-flex align-center justify-center"
                  height="120"
                  ><img
                    :src="l.path"
                    style="max-width: 100%; max-height: 100%; object-fit: contain" /></v-card></v-col></v-row></v-container></v-card-text></v-card
    ></v-dialog>
    <v-dialog v-model="templateDialog" max-width="800"
      ><v-card dark
        ><v-card-title>Vorlage</v-card-title
        ><v-card-text
          ><v-container fluid
            ><v-row
              ><v-col cols="6" sm="4" md="3" v-for="(t, i) in templateImages" :key="i"
                ><v-card hover @click="setTemplate(t)" :class="{ 'active-logo': content.image === t.path }"
                  ><v-img :src="t.path" aspect-ratio="0.7"></v-img>
                  <div class="caption text-center pa-2 white grey--text text--darken-3 font-weight-bold">{{ t.name }}</div></v-card
                ></v-col
              ></v-row
            ></v-container
          ></v-card-text
        ></v-card
      ></v-dialog
    >
  </div>
</template>

<script>
import userService from "@/services/userService"
import html2pdf from "html2pdf.js"
import api from "@/api"

export default {
  name: "CommunicationDashboardPrintGenerator",
  data() {
    return {
      userService,
      isPrinting: false,
      previewScale: 0.5,
      logoDialog: false,
      templateDialog: false,
      dragState: { active: false, element: null, startY: 0, startX: 0, startPosX: 0, startPosY: 0 },
      selectedElement: "text1",
      config: { format: "a4", orientation: "portrait" },
      content: { text1: "", text2: "", text3: "", image: null, logo: "/img/logos/studio/WELL-COME-FIT_LOGO_WHITE.png" },
      templateImages: [
        { name: "Alibey", path: "/img/print/printGeneratorTemplates/alibeyTemplate.jpg" },
        { name: "Group Fitness", path: "/img/print/printGeneratorTemplates/groupFitnessTemplate.jpg" },
        { name: "Hyrox", path: "/img/print/printGeneratorTemplates/hyroxTemplate.jpg" },
        { name: "Renovation", path: "/img/print/printGeneratorTemplates/renovationTemplate.jpg" },
        { name: "Team", path: "/img/print/printGeneratorTemplates/teamTemplate.jpg" },
        { name: "Wellness", path: "/img/print/printGeneratorTemplates/wellnessTemplate.jpg" },
        { name: "Yoga", path: "/img/print/printGeneratorTemplates/yogaTemplate.jpg" },
        { name: "Zumba", path: "/img/print/printGeneratorTemplates/zumbaTemplate.jpg" },
        { name: "Abfall", path: "/img/print/printGeneratorTemplates/waste.jpg" },
        { name: "Parkplatz", path: "/img/print/printGeneratorTemplates/parking.jpg" },
        { name: "Osterhase", path: "/img/print/printGeneratorTemplates/easterBunny.jpg" },
        { name: "Ostereier", path: "/img/print/printGeneratorTemplates/easterEggs.jpg" },
      ],
      pos: { text1: { y: 35 }, text2: { y: 55 }, text3: { y: 80 } },
      imgPos: { x: 50, y: 50 },
      style: { text1: { size: 55, color: "#00943c" }, text2: { size: 25, color: "#000000" }, text3: { size: 18, color: "#000000" } },
      colors: ["#000000", "#333333", "#00943c", "#FFFFFF", "#E63946", "#1D3557"],
    }
  },
  mounted() {
    window.addEventListener("resize", this.calculateScale)
    this.calculateScale()
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.calculateScale)
  },
  watch: {
    "config.format"() {
      this.$nextTick(this.calculateScale)
    },
    "config.orientation"() {
      this.$nextTick(this.calculateScale)
    },
    "style.text1.size"(val) {
      if (this.style.text2.size >= val) this.style.text2.size = val - 5
    },
    "style.text2.size"(val) {
      if (this.style.text3.size >= val) this.style.text3.size = val - 5
      if (val >= this.style.text1.size) this.style.text1.size = val + 5
    },
  },
  computed: {
    availableLogos() {
      let logos = [{ name: "Standard", path: "/img/logos/studio/WELL-COME-FIT_LOGO_WHITE.png" }]
      if (this.userService?.getStudio()) {
        logos.push({
          name: "studio",
          path: "https://app.wellcomefit.ch/img/logos/studio/WELL-COME-FIT_LOGO_" + this.userService.getStudio().code.toUpperCase() + "_WHITE.png",
        })
      }
      return logos
    },
  },
  methods: {
    getTextStyle(element) {
      const y = this.pos[element].y
      const left = 46 - y * 0.25 + (element === "text1" ? 5 : 5)
      return {
        top: y + "%",
        left: left + "%",
        width: 95 - left + "%",
        color: this.style[element].color,
        fontSize: this.style[element].size + "pt",
        fontFamily: "'Saira', sans-serif",
      }
    },
    calculateScale() {
      const container = this.$el.querySelector(".preview-area")
      if (!container || !this.$refs.paper) return
      this.previewScale = Math.min(
        (container.clientWidth - 40) / this.$refs.paper.clientWidth,
        (container.clientHeight - 40) / this.$refs.paper.clientHeight,
        1,
      )
    },
    onFileChange(e) {
      const file = e.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = (ev) => {
        this.content.image = ev.target.result
        this.imgPos = { x: 50, y: 50 }
      }
      reader.readAsDataURL(file)
    },
    setLogo(l) {
      this.content.logo = l.path
      this.logoDialog = false
    },
    setTemplate(t) {
      this.content.image = t.path
      this.templateDialog = false
    },
    removeImage() {
      this.content.image = null
    },
    startDrag(e, el) {
      this.selectedElement = el
      this.dragState = {
        active: true,
        element: el,
        startX: e.clientX,
        startY: e.clientY,
        startPosX: el === "image" ? this.imgPos.x : 0,
        startPosY: el === "image" ? this.imgPos.y : this.pos[el].y,
      }
    },
    onDrag(e) {
      if (!this.dragState.active) return
      if (this.dragState.element === "image") {
        this.imgPos.x = Math.max(0, Math.min(100, this.dragState.startPosX - (e.clientX - this.dragState.startX) * 0.1))
        this.imgPos.y = Math.max(0, Math.min(100, this.dragState.startPosY - (e.clientY - this.dragState.startY) * 0.1))
      } else {
        const dy = ((e.clientY - this.dragState.startY) / (this.$refs.paper.clientHeight * this.previewScale)) * 100
        let newY = this.dragState.startPosY + dy
        const buffer = 5
        let minY = 5,
          maxY = 95
        if (this.dragState.element === "text1") maxY = this.pos.text2.y - buffer
        else if (this.dragState.element === "text2") {
          minY = this.pos.text1.y + buffer
          if (this.content.text3) maxY = this.pos.text3.y - buffer
        } else if (this.dragState.element === "text3") minY = this.pos.text2.y + buffer
        this.pos[this.dragState.element].y = Math.max(minY, Math.min(maxY, newY))
      }
    },
    stopDrag() {
      this.dragState.active = false
    },
    async print() {
      this.selectedElement = null
      this.isPrinting = true
      const paperElement = this.$refs.paper
      const iframe = this.$refs.printFrame

      const getPrintLeft = (y, t1 = false) => 46 - y * 0.25 + (t1 ? 0 : 5) + "%"
      const getPrintWidth = (y, t1 = false) => 100 - (46 - y * 0.25 + (t1 ? 0 : 5)) - 5 + "%"

      const cssStyles = `
        @import url('https://fonts.googleapis.com/css2?family=Saira:ital,wght@0,700;0,900;1,700;1,900&display=swap');
        @page { margin: 0; size: ${this.config.format} ${this.config.orientation}; }
        body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .paper { position: relative; width: 100%; height: 100vh; background: white; overflow: hidden; font-family: 'Saira', sans-serif; }
        
        .image-stripe-bg { position: absolute; top: -2mm; left: -2mm; height: calc(100% + 4mm); width: 51%; clip-path: polygon(0 0, 100% 0, 51% 100%, 0 100%); background: #00943c !important; z-index: 1; }
        .image-shape-container { position: absolute; top: -2mm; left: -2mm; height: calc(100% + 4mm); width: 50%; clip-path: polygon(0 0, 100% 0, 50% 100%, 0 100%); background: #eee !important; z-index: 2; overflow: hidden; }
        
        .image-fill { width: 100%; height: 100%; background-size: cover; background-repeat: no-repeat; }
        .header-logo { position: absolute; top: 7%; left: 48%; width: 20%; z-index: 10; }
        
        /* CORRECTED: Skew -10deg (Matches A4 Portrait Ratio of 0.25 factor) */
        .draggable-text { 
          position: absolute; 
          z-index: 20; 
          line-height: 1.1; 
          font-family: 'Saira', sans-serif !important;
          transform: skewX(-10deg);
          transform-origin: top left;
        }
        
        /* CORRECTED: Counter-skew 10deg */
        .draggable-text span {
          display: block;
          transform: skewX(0deg);
          transform-origin: top left;
          white-space: pre-wrap; 
        }

        .text-level-1 { font-weight: 900; text-transform: uppercase; left: ${getPrintLeft(this.pos.text1.y, true)}; width: ${getPrintWidth(
        this.pos.text1.y,
        true,
      )}; top: ${this.pos.text1.y}%; font-size: ${this.style.text1.size}pt; color: ${this.style.text1.color}; }
        .text-level-2 { font-weight: 700; left: ${getPrintLeft(this.pos.text2.y)}; width: ${getPrintWidth(this.pos.text2.y)}; top: ${
        this.pos.text2.y
      }%; font-size: ${this.style.text2.size}pt; color: ${this.style.text2.color}; }
        .text-level-3 { font-weight: 500; left: ${getPrintLeft(this.pos.text3.y)}; width: ${getPrintWidth(this.pos.text3.y)}; top: ${
        this.pos.text3.y
      }%; font-size: ${this.style.text3.size}pt; color: ${this.style.text3.color}; }
        .drag-guide, .image-drag-hint, .placeholder-text { display: none !important; }
      `

      const doc = iframe.contentWindow.document
      doc.open()
      doc.write(`<html><head><style>${cssStyles}</style></head><body>${paperElement.outerHTML}</body></html>`)
      doc.close()

      setTimeout(async () => {
        try {
          const pdfBase64 = await html2pdf()
            .set({
              margin: 0,
              image: { type: "jpeg", quality: 1.0 },
              html2canvas: { scale: 3, useCORS: true, letterRendering: true },
              jsPDF: { unit: "mm", format: this.config.format, orientation: this.config.orientation },
            })
            .from(paperElement)
            .output("datauristring")
          await api.createPrintInfoMail(pdfBase64)
        } catch (e) {
          console.error(e)
        }
        iframe.contentWindow.focus()
        iframe.contentWindow.print()
        this.isPrinting = false
      }, 1500)
    },
  },
}
</script>

<style lang="scss" scoped>
.full-height-container {
  height: calc(100vh - 64px);
  overflow: hidden;
}
.preview-area {
  background: #1e1e1e;
  height: 100%;
  position: relative;
  overflow: hidden;
}
.paper-scaler {
  transform-origin: center center;
}
.paper {
  background: white;
  position: relative;
  overflow: hidden;
  box-shadow: 0 0 40px rgba(0, 0, 0, 0.5);
  flex-shrink: 0;
  font-family: "Saira", sans-serif !important;
}
.paper.a4.portrait {
  width: 210mm;
  height: 297mm;
}
.paper.a4.landscape {
  width: 297mm;
  height: 210mm;
}
.paper.a3.portrait {
  width: 297mm;
  height: 420mm;
}
.paper.a3.landscape {
  width: 420mm;
  height: 297mm;
}
.paper.a5.portrait {
  width: 148mm;
  height: 210mm;
}
.paper.a5.landscape {
  width: 210mm;
  height: 148mm;
}

/* Bleeding Fix im Scoped Style */
.image-stripe-bg {
  position: absolute;
  top: -2mm;
  left: -2mm;
  height: calc(100% + 4mm);
  width: 50.5%;
  clip-path: polygon(0 0, 100% 0, 50% 100%, 0 100%);
  background: #00943c;
  z-index: 1;
}
.image-shape-container {
  position: absolute;
  top: -2mm;
  left: -2mm;
  height: calc(100% + 4mm);
  width: 50%;
  clip-path: polygon(0 0, 100% 0, 50% 100%, 0 100%);
  background: #eee;
  z-index: 2;
  overflow: hidden;
}

.image-fill {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
}
.header-logo {
  position: absolute;
  top: 7%;
  left: 48%;
  width: 20%;
  z-index: 10;
  object-fit: contain;
}

/* KORREKTUR: Skew -10deg (weniger steil, passt besser zum Faktor 0.25) */
.draggable-text {
  position: absolute;
  z-index: 20;
  cursor: ns-resize;
  font-family: "Saira", sans-serif !important;
  line-height: 1.1;
  /* Kasten kippt wie Backslash \ */
  transform: skewX(-10deg);
  transform-origin: top left;
}

/* KORREKTUR: Text zurückdrehen mit 10deg */
.draggable-text span {
  display: block;
  transform: skewX(0deg);
  transform-origin: top left;
  /* Damit Leerzeilen (Enter) funktionieren: */
  white-space: pre-wrap;
}

.draggable-text.is-active {
  outline: 2px dashed #00943c;
  background: rgba(0, 148, 60, 0.05);
}
.text-level-1 {
  font-weight: 900;
  text-transform: uppercase;
}
.text-level-2 {
  font-weight: 700;
}
.text-level-3 {
  font-weight: 500;
}

.image-drag-hint {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.4);
  padding: 15px;
  border-radius: 50%;
  opacity: 0;
  transition: 0.3s;
  pointer-events: none;
}
.image-shape-container:hover .image-drag-hint {
  opacity: 1;
}
.placeholder-text {
  font-size: 30pt;
  font-weight: 900;
  color: #ddd;
  transform: rotate(-15deg);
}

.element-box {
  background: rgba(255, 255, 255, 0.05);
  padding: 10px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid transparent;
}
.element-box.active {
  border-color: #00943c;
  background: rgba(0, 148, 60, 0.1);
}
.color-dots {
  display: flex;
  gap: 5px;
}
.dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  cursor: pointer;
  border: 1px solid #444;
}
.input-dense >>> .v-input__slot {
  min-height: 0 !important;
}
.section-label {
  font-size: 0.65rem;
  color: #888;
  font-weight: bold;
}
.drag-guide {
  position: absolute;
  right: -25px;
  top: 50%;
  transform: translateY(-50%);
  color: #00943c;
  opacity: 0.5;
}

/* Drag Guide wieder gerade stellen */
.draggable-text .drag-guide {
  transform: skewX(0deg);
}

.active-logo {
  border: 2px solid #00943c !important;
}
</style>
